import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Check, ChevronDown, Search } from "lucide-react";
import { cn } from "@/lib/cn";
import { filterOptions } from "@/lib/filterOptions";

export interface SearchableFilterOption {
  value: string;
  label: string;
  /**
   * Dòng phụ để PHÂN BIỆT hai lựa chọn cùng tên.
   *
   * Danh sách chỉ có tên công ty thì "PPJ GROUP" ở Hà Nội và "PPJ GROUP" ở
   * Bình Dương trông y hệt nhau; hệ cũ hiện kèm địa chỉ và người liên hệ chính
   * vì lý do đó. Dòng phụ cũng được TÌM KIẾM, không chỉ để nhìn.
   */
  sublabel?: string | null;
}

/**
 * Compact toolbar combobox: a labeled trigger plus a searchable list.
 * Native <select> cannot host a search field, and these catalogs already
 * overflow a screen (brands especially).
 */
export function SearchableFilter({
  label,
  value,
  allLabel,
  options,
  onChange,
  className,
  includeAll = true,
  searchPlaceholder,
  searchValue,
  onSearchChange,
  isLoading = false,
  error,
  onRetry,
  footer,
}: {
  label: string;
  value: string;
  allLabel: string;
  options: SearchableFilterOption[];
  onChange: (value: string) => void;
  className?: string;
  includeAll?: boolean;
  searchPlaceholder?: string;
  /**
   * Ô tìm kiếm do NGƯỜI GỌI điều khiển, khi danh mục lớn hơn một lần tải.
   *
   * Mặc định combobox tự lọc trong số lựa chọn đã có. Danh mục vài nghìn dòng
   * thì phải hỏi máy chủ, nên người gọi cầm chuỗi tìm kiếm để đưa vào truy vấn
   * — và ở chế độ đó combobox KHÔNG lọc lại lần nữa: máy chủ có thể khớp trên
   * cột không hiện ra (mã khách, mã số thuế), lọc thêm tại máy sẽ giấu mất
   * đúng dòng người dùng vừa tìm ra.
   *
   * Đừng dựng thêm một ô tìm kiếm thứ hai bên ngoài: người dùng nhìn thấy hai
   * ô cho cùng một việc và không đoán được ô nào làm gì.
   */
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  /** Đang tải danh sách — hiện dòng chờ thay vì "không có kết quả". */
  isLoading?: boolean;
  /**
   * Tải danh sách THẤT BẠI. Không có tham số này thì một danh sách rỗng do lỗi
   * mạng trông y hệt một danh sách rỗng thật, và người dùng đi tạo bản ghi mới
   * cho một khách đã tồn tại.
   */
  error?: string | null;
  onRetry?: () => void;
  /**
   * Hành động phụ ở CHÂN danh sách, ví dụ "+ Tạo khách hàng mới".
   *
   * Nhận `close` để đóng danh sách trước khi mở hộp thoại tạo — nếu không,
   * lớp phủ của danh sách nằm đè lên hộp thoại vừa mở.
   */
  footer?: (close: () => void) => React.ReactNode;
}) {
  const { t } = useTranslation();
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [localQ, setLocalQ] = useState("");
  const remote = onSearchChange !== undefined;
  const q = remote ? searchValue ?? "" : localQ;
  const setQ = (next: string) => (remote ? onSearchChange(next) : setLocalQ(next));
  const [active, setActive] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);

  const selectedLabel = options.find((o) => o.value === value)?.label ?? (value || allLabel);

  const filtered = useMemo(
    () => filterOptions(options, q, { includeAll, allLabel, remote }),
    [allLabel, includeAll, options, q, remote],
  );

  const close = () => {
    setOpen(false);
    setQ("");
    window.requestAnimationFrame(() => triggerRef.current?.focus());
  };

  useLayoutEffect(() => {
    if (!open) {
      setMenuPos(null);
      return;
    }
    const place = () => {
      const box = triggerRef.current?.getBoundingClientRect();
      if (!box) return;
      setMenuPos({ top: box.bottom + 6, left: box.left });
    };
    place();
    setActive(Math.max(0, filtered.findIndex((o) => o.value === value)));
    window.requestAnimationFrame(() => searchRef.current?.focus());
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
      setQ("");
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  useEffect(() => {
    optionRefs.current[active]?.scrollIntoView({ block: "nearest" });
  }, [active]);

  const select = (next: string) => {
    onChange(next);
    close();
  };

  const move = (delta: number) => {
    if (filtered.length === 0) return;
    setActive((i) => (i + delta + filtered.length) % filtered.length);
  };

  const onSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      close();
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      move(1);
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      move(-1);
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      const next = filtered[active];
      if (next) select(next.value);
    }
  };

  return (
    <div ref={rootRef} className={cn("relative w-48", className)}>
      <button
        ref={triggerRef}
        type="button"
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => (open ? close() : setOpen(true))}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            setOpen(true);
          }
        }}
        className={cn(
          "flex h-9 w-full items-center gap-2 rounded-sm border border-line-strong bg-surface pl-3 pr-2.5 text-left text-sm text-ink",
          "transition-colors hover:border-ink-faint focus-visible:border-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-brand)]/15",
        )}
      >
        <span className="min-w-0 flex-1 truncate">{selectedLabel}</span>
        <ChevronDown
          aria-hidden="true"
          className={cn("size-4 shrink-0 text-ink-faint transition-transform", open && "rotate-180")}
        />
      </button>

      {open && menuPos
        ? createPortal(
        <div
          ref={menuRef}
          className="fixed z-[110] w-72 overflow-hidden rounded-md border border-line bg-surface shadow-[var(--shadow-pop)]"
          style={{ top: menuPos.top, left: menuPos.left }}
        >
          <div className="border-b border-line bg-surface p-1.5">
            <label className="flex h-8 items-center gap-2 rounded-sm border border-line px-2">
              <Search aria-hidden="true" className="size-3.5 shrink-0 text-ink-faint" />
              <input
                ref={searchRef}
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setActive(0);
                }}
                onKeyDown={onSearchKeyDown}
                placeholder={searchPlaceholder ?? t("products.filterSearch")}
                aria-label={searchPlaceholder ?? t("products.filterSearch")}
                className="min-w-0 flex-1 bg-transparent text-sm text-ink outline-none placeholder:text-ink-faint"
              />
            </label>
          </div>
          <div
            id={listId}
            role="listbox"
            aria-label={label}
            className="max-h-64 overflow-y-auto p-1"
          >
            {isLoading ? (
              <p className="px-2.5 py-3 text-center text-xs text-ink-faint">{t("common.loading")}</p>
            ) : error ? (
              <div className="px-2.5 py-3 text-center">
                <p className="text-xs text-danger">{error}</p>
                {onRetry ? (
                  <button
                    type="button"
                    onClick={onRetry}
                    className="mt-1 text-2xs text-brand underline"
                  >
                    {t("common.retry")}
                  </button>
                ) : null}
              </div>
            ) : filtered.length === 0 ? (
              <p className="px-2.5 py-3 text-center text-xs text-ink-faint">{t("common.noMatches")}</p>
            ) : (
              filtered.map((option, index) => {
                const selected = option.value === value;
                const isActive = index === active;
                return (
                  <button
                    key={option.value || "__all"}
                    ref={(node) => { optionRefs.current[index] = node; }}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    onMouseEnter={() => setActive(index)}
                    onClick={() => select(option.value)}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-sm px-2.5 py-1.5 text-left text-sm transition-colors",
                      selected ? "bg-brand-tint text-brand-ink" : "text-ink-soft",
                      isActive && !selected && "bg-sunken text-ink",
                    )}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">{option.label}</span>
                      {option.sublabel ? (
                        <span className="block truncate text-2xs text-ink-faint">{option.sublabel}</span>
                      ) : null}
                    </span>
                    {selected ? <Check aria-hidden="true" className="size-4 shrink-0 text-brand" /> : null}
                  </button>
                );
              })
            )}
          </div>
          {footer ? (
            <div className="border-t border-line px-1 py-1">{footer(close)}</div>
          ) : null}
        </div>,
        document.body,
      ) : null}
    </div>
  );
}
